import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Button,
  Badge,
  Input,
  Select,
  Modal,
  FormField,
  ColumnLayout,
  Pagination,
  Alert
} from '@cloudscape-design/components';
import { useRole } from '../contexts/RoleContext';

interface User {
  userId: string;
  email: string;
  givenName: string;
  familyName: string;
  role: 'Admin' | 'Production' | 'User';
  registrationDate: string;
  deviceCount?: number;
  locationListCount?: number;
  subscriptionCount?: number;
  orderCount?: number;
}

export const UserManagement: React.FC = () => {
  const { isAdmin, isLoading: roleLoading } = useRole();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [newRole, setNewRole] = useState<string>('');
  const [updating, setUpdating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchQuery, roleFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // TODO: Call API to load users
      // const response = await apiService.get('/admin/users');
      
      // For now, use mock data
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockUsers: User[] = [
        {
          userId: 'user-001',
          email: 'admin@example.com',
          givenName: 'Admin',
          familyName: 'User',
          role: 'Admin',
          registrationDate: '2025-01-01',
          deviceCount: 5,
          locationListCount: 10,
          subscriptionCount: 3,
          orderCount: 2
        },
        {
          userId: 'user-002',
          email: 'production@example.com',
          givenName: 'Production',
          familyName: 'User',
          role: 'Production',
          registrationDate: '2025-02-01',
          deviceCount: 0,
          locationListCount: 0,
          subscriptionCount: 1,
          orderCount: 0
        },
        {
          userId: 'user-003',
          email: 'john@example.com',
          givenName: 'John',
          familyName: 'Doe',
          role: 'User',
          registrationDate: '2025-03-01',
          deviceCount: 2,
          locationListCount: 3,
          subscriptionCount: 2,
          orderCount: 5
        },
        {
          userId: 'user-004',
          email: 'jane@example.com',
          givenName: 'Jane',
          familyName: 'Smith',
          role: 'User',
          registrationDate: '2026-01-15',
          deviceCount: 1,
          locationListCount: 2,
          subscriptionCount: 1,
          orderCount: 3
        }
      ];
      
      setUsers(mockUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(query) ||
        user.givenName.toLowerCase().includes(query) ||
        user.familyName.toLowerCase().includes(query)
      );
    }
    
    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowModal(true);
  };

  const handleRoleChangeRequest = () => {
    if (newRole === selectedUser?.role) {
      alert('Role has not changed');
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmRoleChange = async () => {
    if (!selectedUser) return;
    
    try {
      setUpdating(true);
      
      // TODO: Call API to update user role
      // await apiService.put(`/admin/users/${selectedUser.userId}/role`, {
      //   role: newRole
      // });
      
      // For now, simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert(`User role updated to ${newRole}`);
      setShowConfirmDialog(false);
      setShowModal(false);
      await loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    } finally {
      setUpdating(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleColors: Record<string, 'red' | 'blue' | 'grey'> = {
      Admin: 'red',
      Production: 'blue',
      User: 'grey'
    };
    
    return <Badge color={roleColors[role] || 'grey'}>{role}</Badge>;
  };

  // Pagination
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Check if user has permission
  if (!roleLoading && !isAdmin) {
    return (
      <Container>
        <Alert type="error">
          You do not have permission to access user management.
        </Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Manage user accounts and roles"
        actions={
          <Button onClick={loadUsers} iconName="refresh">
            Refresh
          </Button>
        }
      >
        User Management
      </Header>
      
      <Container>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Search">
              <Input
                value={searchQuery}
                onChange={({ detail }) => setSearchQuery(detail.value)}
                placeholder="Search by email or name..."
                type="search"
              />
            </FormField>
            
            <FormField label="Role Filter">
              <Select
                selectedOption={{ label: roleFilter === 'all' ? 'All Roles' : roleFilter, value: roleFilter }}
                onChange={({ detail }) => setRoleFilter(detail.selectedOption.value || 'all')}
                options={[
                  { label: 'All Roles', value: 'all' },
                  { label: 'Admin', value: 'Admin' },
                  { label: 'Production', value: 'Production' },
                  { label: 'User', value: 'User' }
                ]}
              />
            </FormField>
          </ColumnLayout>
        </SpaceBetween>
      </Container>
      
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'email',
              header: 'Email',
              cell: (item: User) => item.email,
              sortingField: 'email'
            },
            {
              id: 'name',
              header: 'Name',
              cell: (item: User) => `${item.givenName} ${item.familyName}`,
              sortingField: 'givenName'
            },
            {
              id: 'role',
              header: 'Role',
              cell: (item: User) => getRoleBadge(item.role),
              sortingField: 'role'
            },
            {
              id: 'registrationDate',
              header: 'Registration Date',
              cell: (item: User) => new Date(item.registrationDate).toLocaleDateString(),
              sortingField: 'registrationDate'
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item: User) => (
                <Button onClick={() => handleViewUser(item)}>
                  View Details
                </Button>
              )
            }
          ]}
          items={paginatedUsers}
          loading={loading}
          loadingText="Loading users..."
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No users</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No users match the current filters.
              </Box>
            </Box>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={Math.ceil(filteredUsers.length / itemsPerPage)}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          }
        />
      </Container>
      
      {/* User Details Modal */}
      <Modal
        visible={showModal}
        onDismiss={() => setShowModal(false)}
        header="User Details"
        size="large"
      >
        {selectedUser && (
          <SpaceBetween size="l">
            <ColumnLayout columns={2}>
              <div>
                <Box variant="awsui-key-label">Email</Box>
                <div>{selectedUser.email}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Name</Box>
                <div>{`${selectedUser.givenName} ${selectedUser.familyName}`}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Current Role</Box>
                <div>{getRoleBadge(selectedUser.role)}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Registration Date</Box>
                <div>{new Date(selectedUser.registrationDate).toLocaleDateString()}</div>
              </div>
            </ColumnLayout>
            
            <Container header={<Header variant="h3">User Statistics</Header>}>
              <ColumnLayout columns={4}>
                <div>
                  <Box variant="awsui-key-label">Devices</Box>
                  <div>{selectedUser.deviceCount || 0}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Location Lists</Box>
                  <div>{selectedUser.locationListCount || 0}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Subscriptions</Box>
                  <div>{selectedUser.subscriptionCount || 0}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Orders</Box>
                  <div>{selectedUser.orderCount || 0}</div>
                </div>
              </ColumnLayout>
            </Container>
            
            <FormField label="Change Role">
              <Select
                selectedOption={{ label: newRole, value: newRole }}
                onChange={({ detail }) => setNewRole(detail.selectedOption.value || '')}
                options={[
                  { label: 'Admin', value: 'Admin' },
                  { label: 'Production', value: 'Production' },
                  { label: 'User', value: 'User' }
                ]}
              />
            </FormField>
            
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRoleChangeRequest}
                disabled={newRole === selectedUser.role}
              >
                Change Role
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        )}
      </Modal>
      
      {/* Confirmation Dialog */}
      <Modal
        visible={showConfirmDialog}
        onDismiss={() => setShowConfirmDialog(false)}
        header="Confirm Role Change"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleConfirmRoleChange}
                loading={updating}
              >
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Alert type="warning">
          Are you sure you want to change {selectedUser?.email}'s role from{' '}
          <strong>{selectedUser?.role}</strong> to <strong>{newRole}</strong>?
          <br /><br />
          This action will immediately affect the user's permissions and access to system resources.
        </Alert>
      </Modal>
    </SpaceBetween>
  );
};

export default UserManagement;
